import React, { useState, useEffect } from 'react';
import { Input } from '../Input';
import { INPUT_PLACEHOLDERS, KEYBOARD_KEYS } from '../../constants/common';

type Numeric2 = string & { readonly __numeric2: unique symbol };

interface ICompressedDateInputProps {
  id?: string;
  label?: string;
  name?: string;
  className?: string;
  initialValue?: string;
  onChange: (data: { month: Numeric2; year: Numeric2 }) => void;
}

export const CompressedDateInput: React.FC<ICompressedDateInputProps> = ({
  id = '',
  label = '',
  name = '',
  className = '',
  initialValue = '',
  onChange,
}) => {
  const [digits, setDigits] = useState('');

  const asNumeric2 = (value: string): Numeric2 | null => (/^\d{2}$/.test(value) ? (value as Numeric2) : null);

  const format = (digits: string) => {
    if (digits.length <= 2) return digits;
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}`;
  };

  const unformat = (value: string) => value.replace(/\D/g, '');

  useEffect(() => {
    if (initialValue) {
      setDigits(initialValue.replace(/\D/g, ''));
    }
  }, [initialValue]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawDigits = unformat(e.target.value).slice(0, 4);

    setDigits(rawDigits);

    if (rawDigits.length === 4) {
      const month = asNumeric2(rawDigits.slice(0, 2));
      const year = asNumeric2(rawDigits.slice(2, 4));

      if (month && year) {
        onChange({ month, year });
      }
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== KEYBOARD_KEYS.BACKSPACE) return;

    const input = event.currentTarget;
    const cursor = input.selectionStart ?? 0;

    // If cursor is right after slash: MM/|YY
    if (cursor === 3) {
      event.preventDefault();
      setDigits(prev => prev.slice(0, 1)); // delete second digit
    }
  };

  return (
    <Input
      id={id}
      type="text"
      label={label}
      name={name}
      maxLength={5}
      placeholder={INPUT_PLACEHOLDERS.COMPRESSED_DATE_INPUT}
      value={format(digits)}
      className={`compressed-date-input ${className}`}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      varient="outlined"
    />
  );
};
